import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import React, { useEffect, useId, useMemo, useRef } from 'react';

import CheckReservation from '@/components/Reservation/Accept/CheckReservation/CheckReservation';
import EtcInfo from '@/components/Reservation/Accept/EtcInfo/EtcInfo';
import FooterSub from '@/components/FooterSub/FooterSub';
import HeaderSub from '@/components/HeaderSub/HeaderSub';
import MenuInfo from '@/components/Reservation/Accept/MenuInfo/MenuInfo';
import MessageTemplateInfo from '@/components/Reservation/Accept/MessageTemplateInfo/MessageTemplateInfo';
import Modal from '@/components/Modal/Modal';
import ModalPortal from '@/components/ModalPortal/ModalPortal';
import ReservationInfo from '@/components/Reservation/Accept/ReservationInfo/ReservationInfo';
import UserInfo from '@/components/Reservation/Accept/UserInfo/UserInfo';
import { devServerApi } from '@/shared/apis/devServerApi';
import { reservationSchema } from '@/hook-form/schema/reservation';
import { useLayoutStore } from '@/stores/common/useLayoutStore';
import { useModalStore } from '@/stores/common/useModalStore';
import { useReservationStore } from '@/stores/reservation/useReservationStore';
import { yupResolver } from '@hookform/resolvers/yup';
import Terces from '@/shared/apis/terces';
import { instantDisabler } from '@/shared/lib/functions';

const INITIAL_STEP_TEXTS = {
  header: (
    <>
      예약 정보 확인 및 <br />
      상세 정보를 입력 해주세요.
    </>
  ),
  footer: { back: '뒤로', success: '예약완료' },
};

const Step5 = (): React.ReactElement => {
  const keyboard = useLayoutStore((s) => s.keyboard);
  const { visitDate, visitTime, parties, rooms, menus, reservationInfo, userInfo, setReservationUpdateStore, setReservationResetStore } = useReservationStore();

  const { setAlertModal } = useModalStore();
  const { fromApp, setFromApp } = useLayoutStore();
  const navigate = useNavigate();

  const requiredModalId = useId();
  const closeConfirmModalId = useId();
  const errorMessage = useRef('');

  const validationSchema = useMemo(() => reservationSchema, []);

  const methods = useForm<any>({
    defaultValues: {
      visitDate,
      visitTime,
      parties,
      rooms,
      ...reservationInfo,
      ...userInfo,
      alarmMessageTemplate: userInfo?.alarmMessageTemplate,
      creationMessageTemplate: userInfo?.creationMessageTemplate,
      useDeposit: false,
      // ...depositInfo,
      // depositPaymentType: depositInfo?.depositPaymentType,
      // depositDue: depositInfo?.depositDue,
      // depositEmailLanguage: depositInfo?.depositEmailLanguage,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const { handleSubmit, getValues } = methods;

  const handleMoveRoute = (path) => {
    setReservationUpdateStore({ ...getValues(), menus });
    navigate(path);
  };

  const handleOnClose = () => {
    setAlertModal({ visible: true, key: closeConfirmModalId });
  };

  const handleOnSubmit = (data) => {
    const result = {
      ...data,
      visitDate: data.visitDate.format('YYYY-MM-DD'),
      visitTime: Number(data.visitTime.hour() * 60 + data.visitTime.minute()),
    };

    if (!result.alarmMessageTemplate) delete result.alarmMessageTemplate;
    if (!result.creationMessageTemplate) delete result.creationMessageTemplate;

    result.reservationExtravalues = result.reservationExtravalues.filter((item) => item.value);
    result.reservationExtravalues = result.reservationExtravalues.map((item) => ({ id: item.id, name: item.name, value: item.value }));

    result.userExtravalues = result.userExtravalues.filter((item) => item.value);
    result.userExtravalues = result.userExtravalues.map((item) => ({ id: item.id, name: item.name, value: item.value }));

    result.menus = menus;
    devServerApi.api
      .post('/reservations', Terces.encrypt(result))
      .then(() => {
        setReservationResetStore();
        if (fromApp) {
          setFromApp(null);
          if (window.NavigateToHistory) {
            window.NavigateToHistory.postMessage('');
          } else {
            navigate(`/reservation/list?date=${result.visitDate}`);
          }
        } else {
          navigate(`/reservation/list?date=${result.visitDate}`);
        }
      })
      .catch((e) => console.log(e));
  };

  const handleOnError = (errors) => {
    console.log(errors);
    const error = errors[Object.keys(errors)[0]];
    const refName = errors[Object.keys(errors)[0]].ref?.name;
    errorMessage.current = error.message;
    if (refName) {
      document.getElementsByName(refName)[0].scrollIntoView();
      window.scrollTo(0, window.scrollY - 76);
    } else {
      window.scrollTo(0, 0);
    }
    setAlertModal({ visible: true, key: requiredModalId });
  };

  useEffect(() => {
    document.querySelector('div.main-container-desktop').scrollTo(0, 0);
  }, []);

  return (
    <>
      <FormProvider {...methods}>
        <HeaderSub onClose={handleOnClose} isScrollTitleVisible>
          {INITIAL_STEP_TEXTS.header}
        </HeaderSub>
        <section className="reservation-check-wrapper">
          <CheckReservation handleMoveRoute={handleMoveRoute} />
        </section>

        <section className="reservation-detail-wrapper">
          <div className="font__body_1 pb-4">
            예약 상세정보 입력<span className="guide-message">(추후 입력/수정 가능)</span>
          </div>
          <MessageTemplateInfo />
          <ReservationInfo />
          <UserInfo />
          <EtcInfo />
        </section>

        <FooterSub isBackArea={!keyboard} isVisible style={keyboard ? { display: 'none' } : {}}>
          <div className="flex-container gap-8 w-full">
            <Link to="/reservation/accept/step-4/" className="w-full">
              <button className="footer tblm-button-normal w-full">{INITIAL_STEP_TEXTS.footer.back}</button>
            </Link>
            <button onClick={instantDisabler(handleSubmit(handleOnSubmit, handleOnError))} className="footer tblm-button-normal w-full tblm-btn-primary">
              {INITIAL_STEP_TEXTS.footer.success}
            </button>
          </div>
        </FooterSub>
      </FormProvider>
      <ModalPortal>
        <Modal.Alert
          key={requiredModalId}
          modal="alert"
          isDim={true}
          isAnimation={true}
          modalData={{
            title: '알림',
            content: errorMessage.current ?? '예약 필수 정보를 입력해주세요.',
            buttons: [
              {
                text: '확인',
                success: true,
                onClick: () => setAlertModal({ visible: false, key: requiredModalId }),
              },
            ],
          }}
        />
        <Modal.Alert
          key={closeConfirmModalId}
          modal="alert"
          isDim={true}
          isAnimation={true}
          modalData={{
            title: '접수를 취소하시겠습니까?',
            content: '현재 작성중인 접수를 취소하면\n복구가 불가능합니다.',
            buttons: [
              {
                text: '닫기',
                onClick: () => setAlertModal({ visible: false, key: closeConfirmModalId }),
              },
              {
                text: '접수 취소',
                success: true,
                onClick: () => {
                  setAlertModal({ visible: false, key: closeConfirmModalId });
                  setReservationResetStore();
                  navigate('/reservation/list');
                  setFromApp(null);
                },
              },
            ],
          }}
        />
      </ModalPortal>
    </>
  );
};

export default Step5;
